import React, { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';

import PopOverMenu from '../../../../../components/PopOverMenu';
import SearchableMultiSelectPopOverMenu from '../../../../../components/SearchableMultiSelectPopOverMenu';
import { SortType } from '../../../../../components/ColumnHeader';
import DebouncedFilterInput from '../../../../../components/FormElementsStandalone/DebouncedFilterInput';

import { getDefaultSortOrderForDivision, getDefaultSortParamForDivision, getDefaultSortParamForPickleballDivision, shouldDisplayAllDivisionsFilterInPlayerList } from '../../../../../utils/events';
import { setPlayersTabState, getDivisionAcceptanceList } from '../../../../../actions/event-profile-actions';
import { acceptanceBucketTypes, dateFormat, registrationGroupStatuses, sportTypeIds, waitlistBucketTypes, playerListDataTypes } from '../../../../../utils/eventConstants';
import { sortParamTypes } from '../../../../../utils/constants';

import styles from '../../../event.scss';
import { divisionShape } from '../../../../../propTypes';

const PlayerFilters = forwardRef((props, ref) => {
    const { eventDivisions, membersWithoutProfile, sportTypeId, isCurrentMemberAdmin, playerPositions, playerStatuses, dataType, setDataType, updateQueryParam, hasPublishedDraw } = props;

    const { event, playersTabState, acceptance } = useSelector(({ eventProfile }) => {
        const { event, playersTabState, acceptance } = eventProfile;
        return { event, playersTabState, acceptance };
    });
    const dispatch = useDispatch();

    const eventId = event.data?.id;

    const getDivisionName = (divisionName, specificDivisionDateEnabled, specificDivisionDateUtc) => {
        const labels = [divisionName];
        const showSpecificDivisionDate = specificDivisionDateEnabled && specificDivisionDateUtc;
        if (showSpecificDivisionDate) {
            const timeZone = event.data?.eventSchedule?.timeZone;
            labels.push(moment.utc(specificDivisionDateUtc).tz(timeZone?.value).format(dateFormat));
        }
        return labels.join(' \u00B7 ');
    };

    const shouldShowAllDivisionsFilter = shouldDisplayAllDivisionsFilterInPlayerList(eventDivisions);

    const divisionFilterItems = eventDivisions.filter(div => !div.isEventEntryFeeDivision).map(div => ({
        value: div.id,
        label: getDivisionName(div.name) === 'singles' ? 'Singles' : getDivisionName(div.name),
        divisionName: div.name === 'singles' ? 'Singles' : div.name,
        divisionFormatType: div.divisionFormatType,
        specificDivisionDateEnabled: div.specificDivisionDateEnabled,
        specificDivisionDateUtc: div.specificDivisionDateUtc,
        onlySingleSelect: !shouldShowAllDivisionsFilter,
    }));

    const divisionMenuItems = [
        ...(shouldShowAllDivisionsFilter ? [{ value: '', label: 'All divisions', onlySingleSelect: true }] : []),
        ...divisionFilterItems,
    ];
    if (membersWithoutProfile?.length) {
        divisionMenuItems.push({ value: 'ERRORS', label: 'Registration Errors', onlySingleSelect: true });
    }

    const playerStatusMenuItems = [
        { value: '', label: 'All statuses' },
        ...(playerStatuses[registrationGroupStatuses.REGISTERED] ? [{ value: registrationGroupStatuses.REGISTERED, label: 'Registered' }] : []),
        ...(playerStatuses[registrationGroupStatuses.NEED_PARTNER] ? [{ value: registrationGroupStatuses.NEED_PARTNER, label: 'Need partner' }] : []),
        ...(playerStatuses[registrationGroupStatuses.PENDING] ? [{ value: registrationGroupStatuses.PENDING, label: 'Pending' }] : []),
        ...(playerStatuses[registrationGroupStatuses.WITHDRAWN] ? [{ value: registrationGroupStatuses.WITHDRAWN, label: 'Withdrawn' }] : []),
    ];

    const playerPositionMenuItems = [
        { value: '', label: 'All positions' },
        ...(playerPositions[waitlistBucketTypes.MAIN] ? [{ value: waitlistBucketTypes.MAIN, label: 'Main' }] : []),
        ...(playerPositions[waitlistBucketTypes.WAITLIST] ? [{ value: waitlistBucketTypes.WAITLIST, label: 'Waitlist' }] : []),
        ...(playerPositions[waitlistBucketTypes.NEED_PARTNER] ? [{ value: waitlistBucketTypes.NEED_PARTNER, label: 'Need partner' }] : []),
        ...(playerPositions[acceptanceBucketTypes.QUALIFYING] ? [{ value: acceptanceBucketTypes.QUALIFYING, label: 'Qualifying' }] : []),
        ...(playerPositions[acceptanceBucketTypes.ALTERNATE] ? [{ value: acceptanceBucketTypes.ALTERNATE, label: 'Alternate' }] : []),
        ...(playerPositions[waitlistBucketTypes.WITHDRAWN] ? [{ value: waitlistBucketTypes.WITHDRAWN, label: 'Withdrawn' }] : []),
    ];

    const dataTypeMenuItems = [
        { value: '', label: 'View all data' },
        { value: playerListDataTypes.DRAWS, label: 'Draws only' },
        { value: playerListDataTypes.DIVISIONS, label: 'Divisions only' },
    ];

    const onSelectDivision = (selected) => {
        updateQueryParam('divisionId', selected.value);
        const eventDivision = eventDivisions.find(div => div.id === selected.value);
        const update = {};
        if (!selected.value) {
            update.divisionId = '';
            update.divisionName = 'All divisions';
            update.playerListDivisionFilters = [];
        } else if (selected.value === 'ERRORS') {
            update.divisionId = selected.value;
            update.divisionName = eventDivision?.name;
            update.divisionFormatTypeId = eventDivision?.divisionFormatTypeId;
            update.teamPaymentTypeId = eventDivision?.teamPaymentTypeId;
            update.registeredCount = (membersWithoutProfile?.length) || 0;
            update.selectedStatus = {
                value: '',
                label: 'All statuses',
            };
            update.specificDivisionDateEnabled = eventDivision?.specificDivisionDateEnabled;
            update.specificDivisionDateUtc = eventDivision?.specificDivisionDateUtc;
            update.playerListDivisionFilters = [];
        } else {
            update.divisionId = selected.value;
            update.divisionName = eventDivision?.name;
            update.divisionFormatTypeId = eventDivision?.divisionFormatTypeId;
            update.teamPaymentTypeId = eventDivision?.teamPaymentTypeId;
            update.registeredCount = eventDivision?.registeredCount;
            update.selectedStatus = {
                value: '',
                label: 'All statuses',
            };
            update.selectedPosition = {
                value: '',
                label: 'All positions',
            };
            update.specificDivisionDateEnabled = eventDivision?.specificDivisionDateEnabled;
            update.specificDivisionDateUtc = eventDivision?.specificDivisionDateUtc;
            update.playerListDivisionFilters = [selected.value];
        }
        if (eventDivision?.allowWaitlist) {
            update.sortParam = sortParamTypes.REGDATE;
            update.sortType = SortType.ASC;
        } else {
            update.sortParam = sportTypeId === sportTypeIds.PICKLEBALL ? getDefaultSortParamForPickleballDivision(eventDivision) : getDefaultSortParamForDivision(eventDivision);
            update.sortType = getDefaultSortOrderForDivision(eventDivision);
        }
        dispatch(setPlayersTabState({
            ...playersTabState,
            ...update,
        }));
        if (eventId && selected.value && eventDivision?.isAcceptance && !acceptance?.[selected.value]) {
            dispatch(getDivisionAcceptanceList({ eventId, divisionId: selected.value }));
        }
    };

    const onSelectDivisions = (selectedDivisions) => {
        if (selectedDivisions.length === 1) {
            onSelectDivision(selectedDivisions[0]);
            return;
        }

        const selectedDivisionIds = selectedDivisions.map(div => div.value);
        updateQueryParam('divisionId', '');
        const update = {
            playerListDivisionFilters: selectedDivisionIds,
            divisionId: '',
            divisionName: 'Multiple divisions',
            sortParam: sportTypeId === sportTypeIds.PICKLEBALL ? sortParamTypes.SINGLESPKB : sortParamTypes.SINGLESUTR,
            sortType: SortType.DESC,
        };
        dispatch(setPlayersTabState({
            ...playersTabState,
            ...update,
        }));
    };

    const onSelectPlayerStatus = (selected) => {
        dispatch(setPlayersTabState({
            ...playersTabState,
            selectedStatus: selected,
        }));
    };
    const onSelectPlayerPosition = (selected) => {
        dispatch(setPlayersTabState({
            ...playersTabState,
            selectedPosition: selected,
        }));
    };
    const onFilterQueryChange = (value) => {
        dispatch(setPlayersTabState({
            ...playersTabState,
            filterQuery: value,
        }));
    };

    const isFilterActive = playersTabState.divisionId || playersTabState.selectedStatus?.value || playersTabState.selectedPosition?.value || playersTabState.playerListDivisionFilters?.length > 0;

    const showDataTypeFilter = hasPublishedDraw && playersTabState.divisionId === '' && event.data?.divisionDrawAssociation;

    const onResetClick = () => {
        const divisionIdToSet = shouldShowAllDivisionsFilter ? '' : playersTabState.divisionId;
        const eventDivision = eventDivisions.find(div => div.id === parseInt(divisionIdToSet, 10));
        updateQueryParam('divisionId', divisionIdToSet);
        const update = {
            divisionId: divisionIdToSet,
            playerListDivisionFilters: [],
            divisionName: eventDivision?.name || 'All Divisions',
            selectedStatus: {
                value: '',
                label: 'All statuses',
            },
            selectedPosition: {
                value: '',
                label: 'All positions',
            },
            sortParam: sportTypeId === sportTypeIds.PICKLEBALL ? sortParamTypes.SINGLESPKB : sortParamTypes.SINGLESUTR,
            sortType: SortType.DESC,
        };
        dispatch(setPlayersTabState({
            ...playersTabState,
            ...update,
        }));
    };

    const getDataTypeFilterLabel = () => {
        return dataTypeMenuItems.find(item => item.value === dataType)?.label;
    };

    const onSelectDataType = ({ value }) => {
        setDataType(value);
    };

    useImperativeHandle(ref, () => ({
        onSelectDivisions,
    }));

    return (
        <div className={`d-flex mt24 mb12 noselect ${styles.filters}`}>
            {divisionMenuItems.length === 1 && (
                <div className={`${styles.anchorClass} ${styles.filter}`}>
                    <div className="ellipsis" title={divisionMenuItems[0].label}>
                        {divisionMenuItems[0].label}
                    </div>
                </div>
            )}
            {divisionMenuItems.length > 1 && (
                <div className={`${styles.filter} mw0`}>
                    <SearchableMultiSelectPopOverMenu
                        menuItems={divisionMenuItems}
                        onSelected={onSelectDivisions}
                        selectedValues={playersTabState?.playerListDivisionFilters}
                        alignLeft={true}
                        attributeToSave="value"
                        attributeToShow="label"
                        isSearchable={true}
                    >
                        <div className={`${styles.anchorClass}`}>
                            <div className={`ellipsis`}>
                                {playersTabState && (
                                    getDivisionName(playersTabState.divisionName)
                                )}
                            </div>
                            <i className={`${styles.arrow} uts uts-expand`} />
                        </div>
                    </SearchableMultiSelectPopOverMenu>
                </div>
            )}
            {isCurrentMemberAdmin && (
                <div className={styles.filter}>
                    <PopOverMenu
                        anchorClass={`${styles.anchorClass}`}
                        popOverMenuClass="flex-right"
                        menuItems={playerStatusMenuItems}
                        onSelected={onSelectPlayerStatus}
                        alignLeft={true}
                        data-testid="event-players.filters.status-menu"
                    >
                        {playersTabState?.selectedStatus?.label}
                        <i className={`${styles.arrow} uts uts-expand`} />
                    </PopOverMenu>
                </div>
            )}
            <div className={styles.filter}>
                <PopOverMenu
                    anchorClass={`${styles.anchorClass}`}
                    popOverMenuClass="flex-right"
                    menuItems={playerPositionMenuItems}
                    onSelected={onSelectPlayerPosition}
                    alignLeft={true}
                    data-testid="event-players.filters.position-menu"
                >
                    {playersTabState?.selectedPosition?.label}
                    <i className={`${styles.arrow} uts uts-expand`} />
                </PopOverMenu>
            </div>
            {showDataTypeFilter && (
                <div className={styles.filter}>
                    <PopOverMenu
                        anchorClass={`${styles.anchorClass}`}
                        popOverMenuClass="flex-right"
                        menuItems={dataTypeMenuItems}
                        onSelected={onSelectDataType}
                        alignLeft={true}
                        data-testid="event-players.filters.data-type-menu"
                    >
                        {getDataTypeFilterLabel()}
                        <i className={`${styles.arrow} uts uts-expand`} />
                    </PopOverMenu>
                </div>
            )}
            {isFilterActive && (
                <div className={`${styles.filter} clickable link fs14 d-flex aic`} onClick={onResetClick} data-testid="event-players.filters.reset-btn">
                    Reset
                </div>
            )}
            <div className={styles.filterInput}>
                <DebouncedFilterInput
                    className={`${styles.anchorClass}`}
                    placeholder="Find a player"
                    hideSearchIcon
                    onChange={onFilterQueryChange}
                    data-testid="event-players.filters.search-input"
                />
            </div>
        </div>
    );
});

PlayerFilters.propTypes = {
    eventDivisions: PropTypes.arrayOf(divisionShape).isRequired,
    membersWithoutProfile: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
    })),
    sportTypeId: PropTypes.number,
    isCurrentMemberAdmin: PropTypes.bool,
    playerPositions: PropTypes.objectOf(PropTypes.number),
    playerStatuses: PropTypes.objectOf(PropTypes.number),
    dataType: PropTypes.string,
    setDataType: PropTypes.func.isRequired,
    updateQueryParam: PropTypes.func.isRequired,
    hasPublishedDraw: PropTypes.bool,
};

PlayerFilters.defaultProps = {
    membersWithoutProfile: [],
    sportTypeId: sportTypeIds.TENNIS,
    isCurrentMemberAdmin: false,
    playerPositions: {},
    playerStatuses: {},
    dataType: '',
    hasPublishedDraw: false,
};

export default PlayerFilters;
